import React, { useState } from 'react';
import { Undo2, Radio } from 'lucide-react';
import { GameResult } from '../../../../types';
import { BroadcastStatus } from '../../../../hooks/useLiveBroadcast';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { formatRoundType } from '../../../../utils';

interface MatchInfo {
  competitionName: string;
  categoryName: string;
  drawName: string;
  refereeName: string;
  courtName: string;
  roundType?: string;
  scoringType: string;
  dateTime: string;
}

interface MatchDetailsProps {
  matchInfo: MatchInfo;
  matchDuration: string;
  gameDuration: string;
  gameResults: GameResult[];
  playerAColor: string;
  playerBColor: string;
  onRollback: () => void;
  broadcastStatus?: BroadcastStatus;
}

export function MatchDetails({
  matchInfo,
  matchDuration,
  gameDuration,
  gameResults,
  playerAColor,
  playerBColor,
  onRollback,
  broadcastStatus
}: MatchDetailsProps) {
  const { t } = useTranslation();
  const [headerExpanded, setHeaderExpanded] = useState(false);

  return (
    <div className="bg-white mt-4">
      <button
        onClick={() => setHeaderExpanded(!headerExpanded)}
        className="w-full py-2 px-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex justify-between items-center">
          {/* Rollback + Pastilles des jeux terminés */}
          <div className="flex gap-1 items-center">
            {/* Bouton Rollback */}
            <button
              onClick={(e) => { e.stopPropagation(); onRollback(); }}
              className="bg-black hover:bg-gray-800 text-white transition-colors px-4 py-2 mr-2"
            >
              <Undo2 size={20} />
            </button>

            {/* Broadcast status indicator */}
            {broadcastStatus && broadcastStatus !== 'off' && (
              <div
                className="mr-2 relative"
                title={broadcastStatus === 'active' ? t('screens.liveScore.broadcasting') : t('screens.liveScore.notBroadcasting')}
                role="status"
              >
                <Radio size={18} className={broadcastStatus === 'active' ? 'text-yellow-500' : 'text-gray-400'} />
                {broadcastStatus === 'inactive' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-[2px] h-6 bg-gray-400 rotate-45" />
                  </div>
                )}
              </div>
            )}

            {/* Pastilles des jeux terminés */}
            {gameResults.map((game, index) => {
              const winnerColor = game.winner === 'A' ? playerAColor : playerBColor;
              return (
                <div
                  key={index}
                  className="px-1 py-0.5 text-xs text-white font-medium"
                  style={{ backgroundColor: winnerColor }}
                >
                  {game.scoreA}-{game.scoreB}
                </div>
              );
            })}
          </div>

          {/* Timers */}
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <div className="text-center">
              <div>match</div>
              <div className="font-mono font-semibold">{matchDuration}</div>
            </div>
            <div className="text-center">
              <div>game</div>
              <div className="font-mono font-semibold">{gameDuration}</div>
            </div>
            <span className={`text-gray-400 transition-transform ml-2 ${headerExpanded ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>
        </div>
      </button>
      
      {headerExpanded && (
        <div className="px-4 pb-4">
          {/* Ligne 1 : Competition, Category, Draw */}
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div className="font-semibold text-sm">{matchInfo.competitionName}</div>
            <div className="font-semibold text-sm">{matchInfo.categoryName}</div>
            <div className="font-semibold text-sm">{matchInfo.drawName}</div>
          </div>

          {/* Ligne 2 : Referee, Court, Round type, Scoring type */}
          <div className="grid grid-cols-4 gap-4 mb-3">
            <div className="text-gray-600 text-sm">{matchInfo.refereeName}</div>
            <div className="text-gray-600 text-sm">{matchInfo.courtName}</div>
            <div className="text-gray-600 text-sm">
              {formatRoundType(t, matchInfo.roundType)}
            </div>
            <div className="text-gray-600 text-sm">{matchInfo.scoringType}</div>
          </div>

          {/* Ligne 3 : Date et heure */}
          <div className="text-right">
            <div className="text-gray-500 text-xs">{matchInfo.dateTime}</div>
          </div>
        </div>
      )}
    </div>
  );
}